import { useState, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { 
  Upload, FileSpreadsheet, Download, AlertTriangle, CheckCircle, 
  XCircle, Loader2, ArrowRight, ArrowLeft, Users, User, UserCheck,
  Mail, Link2
} from "lucide-react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const IMPORT_TYPES = [
  {
    id: "households",
    title: "Households & Players",
    description: "Import families with parents and their children",
    icon: <Users className="w-6 h-6" />,
    color: "bg-blue-100 text-blue-600"
  },
  {
    id: "players",
    title: "Players Only",
    description: "Import players and optionally link to existing parents",
    icon: <User className="w-6 h-6" />,
    color: "bg-emerald-100 text-emerald-600"
  },
  {
    id: "coaches",
    title: "Coaches & Staff",
    description: "Import coaches, assistants, and team managers",
    icon: <UserCheck className="w-6 h-6" />,
    color: "bg-purple-100 text-purple-600"
  }
];

const ImportWizard = ({ open, onOpenChange, onImportComplete, initialImportType = null }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(initialImportType ? 2 : 1);
  const [importType, setImportType] = useState(initialImportType);
  const [csvData, setCsvData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [columnMappings, setColumnMappings] = useState([]);
  const [sendEmails, setSendEmails] = useState(true);
  const [linkPlayers, setLinkPlayers] = useState(true);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Update state when initialImportType changes (when dialog opens with pre-selected type)
  useEffect(() => {
    if (open && initialImportType) {
      setImportType(initialImportType);
      setStep(2); // Skip to file upload step
    }
  }, [open, initialImportType]);

  const resetWizard = () => {
    setStep(initialImportType ? 2 : 1);
    setImportType(initialImportType);
    setCsvData(null);
    setFileName("");
    setPreview(null);
    setColumnMappings([]);
    setSendEmails(true);
    setLinkPlayers(true);
    setImportResult(null);
  };

  const handleClose = () => {
    resetWizard();
    onOpenChange(false);
  };

  const downloadTemplate = async () => {
    if (!importType) return;
    
    try {
      const response = await axios.get(`${API}/import/template/${importType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${importType}_import_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvData(event.target?.result);
    };
    reader.readAsText(file);
  }, []);

  const handlePreview = async () => {
    if (!csvData || !importType) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/import/preview`, {
        import_type: importType,
        csv_data: csvData,
        column_mappings: columnMappings.length > 0 ? columnMappings : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreview(response.data);
      setColumnMappings(response.data.column_mappings || []);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview import");
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!csvData || !importType || !preview) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/import/execute`, {
        import_type: importType,
        csv_data: csvData,
        column_mappings: columnMappings,
        send_welcome_emails: sendEmails,
        link_players_to_parents: linkPlayers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImportResult(response.data);
      setStep(5);
      
      if (onImportComplete) {
        onImportComplete(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (csvColumn, field) => {
    setColumnMappings(prev => {
      const existing = prev.findIndex(m => m.csv_column === csvColumn);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { csv_column: csvColumn, field };
        return updated;
      }
      return [...prev, { csv_column: csvColumn, field }];
    });
  };

  const getMappedField = (csvColumn) => {
    return columnMappings.find(m => m.csv_column === csvColumn)?.field || "";
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-slate-600" />
            Import {importType ? IMPORT_TYPES.find(t => t.id === importType)?.title || 'Data' : 'Data'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Choose what type of data you want to import"}
            {step === 2 && "Upload your CSV file"}
            {step === 3 && "Review and map columns"}
            {step === 4 && "Configure import options"}
            {step === 5 && "Import complete"}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl mb-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < 5 && (
                <div className={`w-8 h-0.5 mx-1 transition-colors ${
                  step > s ? 'bg-slate-900' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-1">
          {/* Step 1: Select Import Type */}
          {step === 1 && (
            <div className="space-y-3">
              {IMPORT_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setImportType(type.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    importType === type.id 
                      ? 'border-slate-900 bg-slate-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${type.color}`}>
                      {type.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{type.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{type.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Upload CSV */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Need a template? Download one with sample data.
                </p>
                <Button variant="outline" size="sm" onClick={downloadTemplate} className="rounded-xl">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  csvData ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {csvData ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-emerald-600" />
                    <p className="font-medium text-slate-900">{fileName}</p>
                    <p className="text-sm text-slate-500">File ready for preview</p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setCsvData(null); setFileName(""); }}
                      className="text-slate-500"
                    >
                      Remove and upload different file
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 mx-auto text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-900">Drop your CSV file here</p>
                      <p className="text-sm text-slate-500">or click to browse</p>
                    </div>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="max-w-xs mx-auto"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Preview & Map Columns */}
          {step === 3 && preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">{preview.total_rows}</p>
                  <p className="text-xs text-slate-500">Total Rows</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{preview.valid_rows}</p>
                  <p className="text-xs text-slate-500">Valid</p>
                </div>
                <div className="bg-rose-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-rose-600">{preview.error_rows}</p>
                  <p className="text-xs text-slate-500">Errors</p>
                </div>
              </div>

              {/* Column Mappings */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 mb-3">Column Mappings</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {preview.detected_columns.map((col) => (
                    <div key={col} className="flex items-center gap-3">
                      <span className="text-sm text-slate-600 w-32 truncate">{col}</span>
                      <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                      <Select 
                        value={getMappedField(col)} 
                        onValueChange={(val) => updateMapping(col, val)}
                      >
                        <SelectTrigger className="flex-1 h-8 rounded-lg text-sm">
                          <SelectValue placeholder="Select field..." />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="">-- Skip --</SelectItem>
                          {[...preview.available_fields.required, ...preview.available_fields.optional].map((field) => (
                            <SelectItem key={field} value={field}>
                              {field.replace(/_/g, ' ')}
                              {preview.available_fields.required.includes(field) && ' *'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Table */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <h4 className="font-medium text-slate-900 text-sm">Data Preview (first 5 rows)</h4>
                </div>
                <div className="overflow-x-auto max-h-48">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Status</th>
                        {Object.keys(preview.preview[0]?.data || {}).slice(0, 5).map((key) => (
                          <th key={key} className="px-3 py-2 text-left text-xs font-medium text-slate-500 truncate max-w-[120px]">
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className={`border-t ${row.is_valid ? '' : 'bg-rose-50'}`}>
                          <td className="px-3 py-2">
                            {row.is_valid ? (
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-rose-500" />
                            )}
                          </td>
                          {Object.values(row.data).slice(0, 5).map((val, i) => (
                            <td key={i} className="px-3 py-2 truncate max-w-[120px]">
                              {val || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Errors */}
              {preview.error_rows > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-rose-800">
                        {preview.error_rows} row(s) have validation errors
                      </p>
                      <ul className="mt-2 text-sm text-rose-700 space-y-1">
                        {preview.preview
                          .filter(r => !r.is_valid)
                          .slice(0, 3)
                          .flatMap(r => r.errors)
                          .slice(0, 5)
                          .map((err, i) => (
                            <li key={i}>Row {err.row}: {err.message}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Options */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                <h4 className="font-medium text-slate-900">Import Options</h4>
                
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="sendEmails" 
                    checked={sendEmails} 
                    onCheckedChange={setSendEmails}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="sendEmails" className="font-medium text-slate-900 cursor-pointer">
                      Send welcome emails
                    </Label>
                    <p className="text-sm text-slate-500">
                      New users will receive an email with their login credentials
                    </p>
                  </div>
                </div>

                {(importType === "households" || importType === "players") && (
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="linkPlayers" 
                      checked={linkPlayers} 
                      onCheckedChange={setLinkPlayers}
                      className="mt-1"
                    />
                    <div>
                      <Label htmlFor="linkPlayers" className="font-medium text-slate-900 cursor-pointer">
                        Link players to parents
                      </Label>
                      <p className="text-sm text-slate-500">
                        Match players to existing parent accounts by email
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Ready to import</p>
                    <p className="text-sm text-amber-700 mt-1">
                      {preview?.valid_rows || 0} valid records will be imported.
                      {preview?.error_rows > 0 && ` ${preview.error_rows} rows with errors will be skipped.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Results */}
          {step === 5 && importResult && (
            <div className="space-y-6">
              <div className={`rounded-xl p-6 text-center ${
                importResult.imported > 0 ? 'bg-emerald-50' : 'bg-rose-50'
              }`}>
                {importResult.imported > 0 ? (
                  <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                ) : (
                  <XCircle className="w-16 h-16 mx-auto text-rose-500 mb-4" />
                )}
                <h3 className="text-xl font-semibold text-slate-900">
                  {importResult.imported > 0 ? 'Import Successful!' : 'Import Failed'}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{importResult.imported}</p>
                  <p className="text-sm text-slate-600">Imported</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-slate-600">{importResult.skipped}</p>
                  <p className="text-sm text-slate-600">Skipped</p>
                </div>
              </div>

              {importResult.summary && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-900 mb-3">Summary</h4>
                  <div className="space-y-2 text-sm">
                    {importResult.summary.users_created > 0 && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{importResult.summary.users_created} user accounts created</span>
                      </div>
                    )}
                    {importResult.summary.players_created > 0 && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span>{importResult.summary.players_created} players added</span>
                      </div>
                    )}
                    {importResult.summary.households_created > 0 && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span>{importResult.summary.households_created} households created</span>
                      </div>
                    )}
                    {importResult.summary.coaches_created > 0 && (
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-slate-400" />
                        <span>{importResult.summary.coaches_created} coaches/staff added</span>
                      </div>
                    )}
                    {importResult.emails_sent > 0 && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{importResult.emails_sent} welcome emails sent</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {importResult.errors?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                  <h4 className="font-medium text-rose-800 mb-2">Errors ({importResult.errors.length})</h4>
                  <ul className="text-sm text-rose-700 space-y-1 max-h-32 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>Row {err.row}: {err.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <Button
            variant="ghost"
            onClick={() => step > 1 && step < 5 ? setStep(step - 1) : handleClose()}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 || step === 5 ? 'Close' : 'Back'}
          </Button>

          {step === 1 && (
            <Button 
              onClick={() => setStep(2)} 
              disabled={!importType}
              className="rounded-xl"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 2 && (
            <Button 
              onClick={handlePreview} 
              disabled={!csvData || loading}
              className="rounded-xl"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Preview Import
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 3 && (
            <Button 
              onClick={() => setStep(4)} 
              disabled={preview?.valid_rows === 0}
              className="rounded-xl"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 4 && (
            <Button 
              onClick={handleExecuteImport} 
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import {preview?.valid_rows || 0} Records
            </Button>
          )}

          {step === 5 && (
            <Button onClick={handleClose} className="rounded-xl">
              Done
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportWizard;
